import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  message,
  Typography,
  Tabs,
  Switch,
  Select,
  Space,
  Avatar,
  Divider,
  Tooltip,
  DatePicker,
  Radio,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import {
  UserOutlined,
  LockOutlined,
  SettingOutlined,
  SaveOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const { Title } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

// Types
interface ProfileFormValues {
  name: string;
  email: string;
  phone: string;
}

interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ApplicationFeeConfig {
  applyMode: 'immediate' | 'after_merchant_promo';
  feeType: 'percent' | 'fixed';
  feeRate: number;
  feeAmountVnd: number;
  feeRatePercent?: number;
  minOrderVnd?: number;
  minOrderFeeVnd?: number;
  startsAt: string | null;
  endsAt: string | null;
  isActive?: boolean;
}

interface MerchantMonetizationMenuSlot {
  appVersion: string;
  enabled: boolean;
}

interface MerchantMonetizationMenuConfig {
  current: MerchantMonetizationMenuSlot;
  review: MerchantMonetizationMenuSlot;
}

const defaultMerchantMonetizationMenuConfig =
  (): MerchantMonetizationMenuConfig => ({
    current: { appVersion: '', enabled: true },
    review: { appVersion: '', enabled: true },
  });

interface SystemSettings {
  maintenanceMode: boolean;
  allowRegistration: boolean;
  defaultLanguage: string;
  timezone: string;
  codUnlockThreshold: number;
  orderSlaConfirmedMinutes: number;
  orderSlaPreparingMinutes: number;
  orderSlaReadyMinutes: number;
  orderSlaGraceSeconds: number;
  orderAutoReviewHours: number;
  merchantFreePeriodMonths?: number;
  merchantBannerThresholdDays?: number;
  merchantCreditTopupEnabled?: boolean;
  merchantMonetizationMenuConfig?: MerchantMonetizationMenuConfig;
  applicationFeeConfig?: ApplicationFeeConfig;
  paymentGatewayProvider?: 'appotapay';
  paymentWebhookBaseUrl?: string;
  appotapayPartnerCode?: string;
  appotapayApiKey?: string;
  appotapaySecretKey?: string;
  appotapayEnvironment?: 'sandbox' | 'production';
  appotapayConfigured?: boolean;
}

const Settings: React.FC = () => {
  const { user, logout, refreshUser } = useAuth();
  const [profileForm] = Form.useForm<ProfileFormValues>();
  const [passwordForm] = Form.useForm<PasswordFormValues>();
  const [loading, setLoading] = useState<boolean>(false);
  const [systemSettings, setSystemSettings] = useState<SystemSettings>({
    maintenanceMode: false,
    allowRegistration: true,
    defaultLanguage: 'vi',
    timezone: 'Asia/Ho_Chi_Minh',
    codUnlockThreshold: 3,
    orderSlaConfirmedMinutes: 10,
    orderSlaPreparingMinutes: 10,
    orderSlaReadyMinutes: 10,
    orderSlaGraceSeconds: 60,
    orderAutoReviewHours: 24,
    merchantFreePeriodMonths: 3,
    merchantBannerThresholdDays: 7,
    merchantCreditTopupEnabled: true,
    merchantMonetizationMenuConfig: defaultMerchantMonetizationMenuConfig(),
    applicationFeeConfig: {
      applyMode: 'after_merchant_promo',
      feeType: 'percent',
      feeRate: 0,
      feeAmountVnd: 0,
      minOrderVnd: 0,
      minOrderFeeVnd: 0,
      startsAt: null,
      endsAt: null,
    },
    paymentGatewayProvider: 'appotapay',
    paymentWebhookBaseUrl: '',
    appotapayPartnerCode: '',
    appotapayApiKey: '',
    appotapaySecretKey: '',
    appotapayEnvironment: 'sandbox',
    appotapayConfigured: false,
  });
  const [loadingSettings, setLoadingSettings] = useState<boolean>(false);

  // Load system settings from API
  useEffect(() => {
    const loadSystemSettings = async () => {
      try {
        setLoadingSettings(true);
        const response = await api.get('/system-settings');
        if (response.data.success && response.data.data) {
          setSystemSettings({
            maintenanceMode: response.data.data.maintenanceMode ?? false,
            allowRegistration: response.data.data.allowRegistration ?? true,
            defaultLanguage: response.data.data.defaultLanguage ?? 'vi',
            timezone: response.data.data.timezone ?? 'Asia/Ho_Chi_Minh',
            codUnlockThreshold: response.data.data.codUnlockThreshold ?? 3,
            orderSlaConfirmedMinutes:
              response.data.data.orderSlaConfirmedMinutes ?? 10,
            orderSlaPreparingMinutes:
              response.data.data.orderSlaPreparingMinutes ?? 10,
            orderSlaReadyMinutes: response.data.data.orderSlaReadyMinutes ?? 10,
            orderSlaGraceSeconds: response.data.data.orderSlaGraceSeconds ?? 60,
            orderAutoReviewHours: response.data.data.orderAutoReviewHours ?? 24,
            merchantFreePeriodMonths:
              response.data.data.merchantFreePeriodMonths ?? 3,
            merchantBannerThresholdDays:
              response.data.data.merchantBannerThresholdDays ?? 7,
            merchantCreditTopupEnabled:
              response.data.data.merchantCreditTopupEnabled ?? true,
            merchantMonetizationMenuConfig: {
              current: {
                appVersion:
                  response.data.data.merchantMonetizationMenuConfig?.current
                    ?.appVersion ?? '',
                enabled:
                  response.data.data.merchantMonetizationMenuConfig?.current
                    ?.enabled !== false,
              },
              review: {
                appVersion:
                  response.data.data.merchantMonetizationMenuConfig?.review
                    ?.appVersion ?? '',
                enabled:
                  response.data.data.merchantMonetizationMenuConfig?.review
                    ?.enabled !== false,
              },
            },
            applicationFeeConfig: {
              applyMode:
                response.data.data.applicationFeeConfig?.applyMode ??
                'after_merchant_promo',
              feeType:
                response.data.data.applicationFeeConfig?.feeType ?? 'percent',
              feeRate: response.data.data.applicationFeeConfig?.feeRate ?? 0,
              feeAmountVnd:
                response.data.data.applicationFeeConfig?.feeAmountVnd ?? 0,
              feeRatePercent:
                response.data.data.applicationFeeConfig?.feeRatePercent ??
                (response.data.data.applicationFeeConfig?.feeRate ?? 0) * 100,
              minOrderVnd:
                response.data.data.applicationFeeConfig?.minOrderVnd ?? 0,
              minOrderFeeVnd:
                response.data.data.applicationFeeConfig?.minOrderFeeVnd ?? 0,
              startsAt:
                response.data.data.applicationFeeConfig?.startsAt ?? null,
              endsAt: response.data.data.applicationFeeConfig?.endsAt ?? null,
              isActive:
                response.data.data.applicationFeeConfig?.isActive ?? false,
            },
            paymentGatewayProvider:
              response.data.data.paymentGatewayProvider ?? 'appotapay',
            paymentWebhookBaseUrl:
              response.data.data.paymentWebhookBaseUrl ?? '',
            appotapayPartnerCode: response.data.data.appotapayPartnerCode ?? '',
            appotapayApiKey: response.data.data.appotapayApiKey ?? '',
            appotapaySecretKey: response.data.data.appotapaySecretKey ?? '',
            appotapayEnvironment:
              response.data.data.appotapayEnvironment ?? 'sandbox',
            appotapayConfigured:
              response.data.data.appotapayConfigured ?? false,
          });
        }
      } catch {
        // Ignore settings load errors — form keeps defaults
      } finally {
        setLoadingSettings(false);
      }
    };

    loadSystemSettings();
  }, []);

  useEffect(() => {
    if (user) {
      profileForm.setFieldsValue({
        name: user.name,
        email: user.email,
        phone: user.phone || '',
      });
    }
  }, [user, profileForm]);

  const handleUpdateProfile = async (
    values: ProfileFormValues
  ): Promise<void> => {
    try {
      setLoading(true);
      await api.put('/profile', values);
      await refreshUser();
      message.success('Cập nhật thông tin cá nhân thành công');
    } catch (error: any) {
      message.error(
        error.response?.data?.message || 'Lỗi khi cập nhật thông tin'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (
    values: PasswordFormValues
  ): Promise<void> => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('Mật khẩu xác nhận không khớp');
      return;
    }

    try {
      setLoading(true);
      await api.put('/change-password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      message.success('Đổi mật khẩu thành công');
      passwordForm.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Lỗi khi đổi mật khẩu');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSystemSettings = async (): Promise<void> => {
    try {
      setLoading(true);
      const payload: Record<string, unknown> = {
        maintenanceMode: systemSettings.maintenanceMode,
        allowRegistration: systemSettings.allowRegistration,
        defaultLanguage: systemSettings.defaultLanguage,
        timezone: systemSettings.timezone,
        codUnlockThreshold: systemSettings.codUnlockThreshold,
        merchantCreditTopupEnabled:
          systemSettings.merchantCreditTopupEnabled ?? true,
        merchantMonetizationMenuConfig:
          systemSettings.merchantMonetizationMenuConfig ??
          defaultMerchantMonetizationMenuConfig(),
        orderSlaConfirmedMinutes: systemSettings.orderSlaConfirmedMinutes,
        orderSlaPreparingMinutes: systemSettings.orderSlaPreparingMinutes,
        orderSlaReadyMinutes: systemSettings.orderSlaReadyMinutes,
        orderSlaGraceSeconds: systemSettings.orderSlaGraceSeconds,
        orderAutoReviewHours: systemSettings.orderAutoReviewHours,
      };
      if (user?.role === 'super_admin') {
        payload.merchantFreePeriodMonths =
          systemSettings.merchantFreePeriodMonths;
        payload.merchantBannerThresholdDays =
          systemSettings.merchantBannerThresholdDays;
        payload.applicationFeeConfig = systemSettings.applicationFeeConfig;
        payload.paymentGatewayProvider = systemSettings.paymentGatewayProvider;
        payload.paymentWebhookBaseUrl = systemSettings.paymentWebhookBaseUrl;
        payload.appotapayPartnerCode = systemSettings.appotapayPartnerCode;
        payload.appotapayApiKey = systemSettings.appotapayApiKey;
        if (systemSettings.appotapaySecretKey) {
          payload.appotapaySecretKey = systemSettings.appotapaySecretKey;
        }
        payload.appotapayEnvironment = systemSettings.appotapayEnvironment;
      }
      await api.put('/system-settings', payload);
      message.success('Lưu cài đặt hệ thống thành công');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Lỗi khi lưu cài đặt');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await api.post('/logout');
      logout();
      message.success('Đăng xuất thành công');
    } catch (error: any) {
      message.error('Lỗi khi đăng xuất');
      logout(); // Force logout anyway
    }
  };

  if (!user) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Title level={3}>Vui lòng đăng nhập để xem cài đặt</Title>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Cài đặt</Title>
      </div>

      <Tabs defaultActiveKey="profile" size="large">
        <TabPane
          tab={
            <span>
              <UserOutlined />
              Thông tin cá nhân
            </span>
          }
          key="profile"
        >
          <Card>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '24px',
              }}
            >
              <Avatar
                size={64}
                icon={<UserOutlined />}
                style={{ marginRight: '16px' }}
              />
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {user.name}
                </Title>
                <p style={{ margin: 0, color: '#666' }}>{user.email}</p>
              </div>
            </div>

            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleUpdateProfile}
            >
              <Form.Item
                name="name"
                label="Họ tên"
                rules={[{ required: true, message: 'Vui lòng nhập họ tên' }]}
              >
                <Input placeholder="Nhập họ tên" />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Vui lòng nhập email' },
                  { type: 'email', message: 'Email không hợp lệ' },
                ]}
              >
                <Input placeholder="Nhập email" />
              </Form.Item>

              <Form.Item name="phone" label="Số điện thoại">
                <Input placeholder="Nhập số điện thoại" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  loading={loading}
                >
                  Cập nhật thông tin
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <LockOutlined />
              Đổi mật khẩu
            </span>
          }
          key="password"
        >
          <Card>
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handleChangePassword}
            >
              <Form.Item
                name="currentPassword"
                label="Mật khẩu hiện tại"
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng nhập mật khẩu hiện tại',
                  },
                ]}
              >
                <Input.Password placeholder="Nhập mật khẩu hiện tại" />
              </Form.Item>

              <Form.Item
                name="newPassword"
                label="Mật khẩu mới"
                rules={[
                  { required: true, message: 'Vui lòng nhập mật khẩu mới' },
                  { min: 6, message: 'Mật khẩu phải có ít nhất 6 ký tự' },
                ]}
              >
                <Input.Password placeholder="Nhập mật khẩu mới" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Xác nhận mật khẩu mới"
                rules={[
                  { required: true, message: 'Vui lòng xác nhận mật khẩu mới' },
                ]}
              >
                <Input.Password placeholder="Xác nhận mật khẩu mới" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  icon={<LockOutlined />}
                  htmlType="submit"
                  loading={loading}
                >
                  Đổi mật khẩu
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <SettingOutlined />
              Cài đặt hệ thống
            </span>
          }
          key="system"
        >
          <Card>
            <div style={{ marginBottom: '24px' }}>
              <Title level={4}>Cài đặt chung</Title>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>Chế độ bảo trì</span>
                  <Switch
                    checked={systemSettings.maintenanceMode}
                    onChange={checked =>
                      setSystemSettings(prev => ({
                        ...prev,
                        maintenanceMode: checked,
                      }))
                    }
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>Cho phép đăng ký</span>
                  <Switch
                    checked={systemSettings.allowRegistration}
                    onChange={checked =>
                      setSystemSettings(prev => ({
                        ...prev,
                        allowRegistration: checked,
                      }))
                    }
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>Ngôn ngữ mặc định</span>
                  <Select
                    value={systemSettings.defaultLanguage}
                    onChange={value =>
                      setSystemSettings(prev => ({
                        ...prev,
                        defaultLanguage: value,
                      }))
                    }
                    style={{ width: 150 }}
                  >
                    <Option value="vi">Tiếng Việt</Option>
                    <Option value="en">English</Option>
                  </Select>
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>Múi giờ</span>
                  <Select
                    value={systemSettings.timezone}
                    onChange={value =>
                      setSystemSettings(prev => ({ ...prev, timezone: value }))
                    }
                    style={{ width: 200 }}
                  >
                    <Option value="Asia/Ho_Chi_Minh">Asia/Ho_Chi_Minh</Option>
                    <Option value="UTC">UTC</Option>
                  </Select>
                </div>
              </Space>
            </div>

            <Divider />

            <div style={{ marginBottom: '24px' }}>
              <Title level={4}>Cài đặt thanh toán</Title>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>
                    Số đơn QR để mở COD{' '}
                    <Tooltip title="Khách hàng phải thanh toán thành công bao nhiêu đơn QR/Chuyển khoản trước khi được phép sử dụng COD (Thanh toán khi nhận hàng). Điều này giúp hạn chế spam đơn hàng.">
                      <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                    </Tooltip>
                  </span>
                  <InputNumber
                    min={0}
                    max={100}
                    value={systemSettings.codUnlockThreshold}
                    onChange={value =>
                      setSystemSettings(prev => ({
                        ...prev,
                        codUnlockThreshold: value ?? 3,
                      }))
                    }
                    style={{ width: 100 }}
                    addonAfter="đơn"
                  />
                </div>
                <div style={{ color: '#888', fontSize: '12px' }}>
                  Ví dụ: Nếu đặt là 3, khách hàng phải hoàn thành 3 đơn thanh
                  toán QR thành công trước khi được phép chọn COD.
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 8,
                  }}
                >
                  <span>
                    Cho phép merchant nạp điểm (credit){' '}
                    <Tooltip title="Khi tắt, merchant không thấy nút mua/nạp điểm trên app Merchant. Lịch sử giao dịch và số dư vẫn hiển thị bình thường.">
                      <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                    </Tooltip>
                  </span>
                  <Switch
                    checked={systemSettings.merchantCreditTopupEnabled ?? true}
                    onChange={checked =>
                      setSystemSettings(prev => ({
                        ...prev,
                        merchantCreditTopupEnabled: checked,
                      }))
                    }
                  />
                </div>
                <div style={{ color: '#888', fontSize: '12px' }}>
                  Mặc định bật. Áp dụng cho tất cả merchant trên app Merchant
                  (Quản lý gói / Mua điểm).
                </div>

                <Divider style={{ margin: '16px 0' }} />

                <Title level={5} style={{ margin: 0 }}>
                  Menu Quảng cáo &amp; Quản lý điểm (theo version app)
                </Title>
                <div style={{ color: '#888', fontSize: '12px' }}>
                  App gửi version dạng <code>2.0.13+14</code> (version+build).
                  Bản App Store production dùng slot &quot;Hiện tại&quot;;
                  TestFlight / Apple review (sandbox receipt) dùng slot
                  &quot;Đang review&quot;.
                </div>

                {(['current', 'review'] as const).map(slotKey => {
                  const slotLabel =
                    slotKey === 'current'
                      ? 'Hiện tại (production)'
                      : 'Đang review';
                  const cfg =
                    systemSettings.merchantMonetizationMenuConfig ??
                    defaultMerchantMonetizationMenuConfig();
                  const slot = cfg[slotKey];
                  return (
                    <div
                      key={slotKey}
                      style={{
                        border: '1px solid #f0f0f0',
                        borderRadius: 8,
                        padding: 12,
                        marginTop: 8,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 8,
                        }}
                      >
                        <strong>{slotLabel}</strong>
                        <Switch
                          checked={slot.enabled}
                          onChange={checked =>
                            setSystemSettings(prev => ({
                              ...prev,
                              merchantMonetizationMenuConfig: {
                                ...(prev.merchantMonetizationMenuConfig ??
                                  defaultMerchantMonetizationMenuConfig()),
                                [slotKey]: {
                                  ...(prev.merchantMonetizationMenuConfig ??
                                    defaultMerchantMonetizationMenuConfig())[
                                    slotKey
                                  ],
                                  enabled: checked,
                                },
                              },
                            }))
                          }
                        />
                      </div>
                      <Input
                        placeholder="2.0.13+14"
                        value={slot.appVersion}
                        onChange={e =>
                          setSystemSettings(prev => ({
                            ...prev,
                            merchantMonetizationMenuConfig: {
                              ...(prev.merchantMonetizationMenuConfig ??
                                defaultMerchantMonetizationMenuConfig()),
                              [slotKey]: {
                                ...(prev.merchantMonetizationMenuConfig ??
                                  defaultMerchantMonetizationMenuConfig())[
                                  slotKey
                                ],
                                appVersion: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                    </div>
                  );
                })}
              </Space>
            </div>

            <Divider />

            <div style={{ marginBottom: '24px' }}>
              <Title level={4}>Phí ứng dụng &amp; ưu đãi merchant mới</Title>
              {user.role !== 'super_admin' && (
                <div style={{ color: '#888', fontSize: 12, marginBottom: 12 }}>
                  Chỉ Super Admin mới được thay đổi cấu hình phí.
                </div>
              )}
              <Space
                direction="vertical"
                style={{ width: '100%' }}
                size="middle"
              >
                <Form.Item
                  label="Miễn phí merchant mới (tháng)"
                  style={{ marginBottom: 0 }}
                  extra="Tự động tạo chương trình miễn phí khi mở quán mới."
                >
                  <InputNumber
                    min={0}
                    max={24}
                    value={systemSettings.merchantFreePeriodMonths ?? 3}
                    disabled={user.role !== 'super_admin'}
                    onChange={value =>
                      setSystemSettings(prev => ({
                        ...prev,
                        merchantFreePeriodMonths: value ?? 3,
                      }))
                    }
                    style={{ width: 120 }}
                  />
                </Form.Item>

                <Form.Item
                  label="Thời điểm áp dụng phí"
                  style={{ marginBottom: 0 }}
                >
                  <Radio.Group
                    value={
                      systemSettings.applicationFeeConfig?.applyMode ??
                      'after_merchant_promo'
                    }
                    disabled={user.role !== 'super_admin'}
                    onChange={e =>
                      setSystemSettings(prev => ({
                        ...prev,
                        applicationFeeConfig: {
                          ...(prev.applicationFeeConfig || {
                            feeType: 'percent',
                            feeRate: 0,
                            feeAmountVnd: 0,
                            startsAt: null,
                            endsAt: null,
                          }),
                          applyMode: e.target.value,
                        },
                      }))
                    }
                  >
                    <Radio value="after_merchant_promo">
                      Sau khi chương trình merchant kết thúc
                    </Radio>
                    <Radio value="immediate">
                      Áp dụng ngay (bỏ qua miễn phí)
                    </Radio>
                  </Radio.Group>
                </Form.Item>

                <Form.Item label="Loại phí" style={{ marginBottom: 0 }}>
                  <Radio.Group
                    value={
                      systemSettings.applicationFeeConfig?.feeType ?? 'percent'
                    }
                    disabled={user.role !== 'super_admin'}
                    onChange={e =>
                      setSystemSettings(prev => ({
                        ...prev,
                        applicationFeeConfig: {
                          ...(prev.applicationFeeConfig || {
                            applyMode: 'after_merchant_promo',
                            feeRate: 0,
                            feeAmountVnd: 0,
                            startsAt: null,
                            endsAt: null,
                          }),
                          feeType: e.target.value,
                        },
                      }))
                    }
                  >
                    <Radio value="percent">Phần trăm (%)</Radio>
                    <Radio value="fixed">Số tiền cố định (VND/đơn)</Radio>
                  </Radio.Group>
                </Form.Item>

                {(systemSettings.applicationFeeConfig?.feeType ?? 'percent') ===
                'percent' ? (
                  <Form.Item label="Phí (%)" style={{ marginBottom: 0 }}>
                    <InputNumber
                      min={0}
                      max={100}
                      step={0.1}
                      value={
                        systemSettings.applicationFeeConfig?.feeRatePercent ??
                        (systemSettings.applicationFeeConfig?.feeRate ?? 0) *
                          100
                      }
                      disabled={user.role !== 'super_admin'}
                      addonAfter="%"
                      onChange={value =>
                        setSystemSettings(prev => ({
                          ...prev,
                          applicationFeeConfig: {
                            ...(prev.applicationFeeConfig || {
                              applyMode: 'after_merchant_promo',
                              feeType: 'percent',
                              feeAmountVnd: 0,
                              startsAt: null,
                              endsAt: null,
                            }),
                            feeRate: (value ?? 0) / 100,
                            feeRatePercent: value ?? 0,
                          },
                        }))
                      }
                      style={{ width: 160 }}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item label="Phí (VND/đơn)" style={{ marginBottom: 0 }}>
                    <InputNumber
                      min={0}
                      step={1000}
                      value={
                        systemSettings.applicationFeeConfig?.feeAmountVnd ?? 0
                      }
                      disabled={user.role !== 'super_admin'}
                      formatter={v =>
                        `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={v => Number(String(v).replace(/,/g, ''))}
                      onChange={value =>
                        setSystemSettings(prev => ({
                          ...prev,
                          applicationFeeConfig: {
                            ...(prev.applicationFeeConfig || {
                              applyMode: 'after_merchant_promo',
                              feeType: 'fixed',
                              feeRate: 0,
                              startsAt: null,
                              endsAt: null,
                            }),
                            feeAmountVnd: value ?? 0,
                          },
                        }))
                      }
                      style={{ width: 200 }}
                    />
                  </Form.Item>
                )}

                <Form.Item
                  label="Phí theo mức đơn tối thiểu"
                  style={{ marginBottom: 0 }}
                  tooltip="Đơn có tổng tiền >= mức tối thiểu sẽ thu phí cố định này thay cho % hoặc phí cố định thông thường"
                >
                  <Space wrap>
                    <InputNumber
                      min={0}
                      step={1000}
                      placeholder="VD: 20000"
                      value={
                        systemSettings.applicationFeeConfig?.minOrderVnd ?? 0
                      }
                      disabled={user.role !== 'super_admin'}
                      addonBefore="Đơn từ"
                      addonAfter="đ"
                      formatter={v =>
                        `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={v => Number(String(v).replace(/,/g, ''))}
                      onChange={value =>
                        setSystemSettings(prev => ({
                          ...prev,
                          applicationFeeConfig: {
                            ...(prev.applicationFeeConfig || {
                              applyMode: 'after_merchant_promo',
                              feeType: 'percent',
                              feeRate: 0,
                              feeAmountVnd: 0,
                              startsAt: null,
                              endsAt: null,
                            }),
                            minOrderVnd: value ?? 0,
                          },
                        }))
                      }
                      style={{ width: 220 }}
                    />
                    <InputNumber
                      min={0}
                      step={1000}
                      placeholder="VD: 2000"
                      value={
                        systemSettings.applicationFeeConfig?.minOrderFeeVnd ?? 0
                      }
                      disabled={user.role !== 'super_admin'}
                      addonBefore="Phí"
                      addonAfter="đ"
                      formatter={v =>
                        `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={v => Number(String(v).replace(/,/g, ''))}
                      onChange={value =>
                        setSystemSettings(prev => ({
                          ...prev,
                          applicationFeeConfig: {
                            ...(prev.applicationFeeConfig || {
                              applyMode: 'after_merchant_promo',
                              feeType: 'percent',
                              feeRate: 0,
                              feeAmountVnd: 0,
                              startsAt: null,
                              endsAt: null,
                            }),
                            minOrderFeeVnd: value ?? 0,
                          },
                        }))
                      }
                      style={{ width: 200 }}
                    />
                  </Space>
                </Form.Item>

                <Form.Item
                  label="Thời gian áp dụng"
                  style={{ marginBottom: 0 }}
                >
                  <DatePicker.RangePicker
                    disabled={user.role !== 'super_admin'}
                    value={[
                      systemSettings.applicationFeeConfig?.startsAt
                        ? dayjs(systemSettings.applicationFeeConfig.startsAt)
                        : null,
                      systemSettings.applicationFeeConfig?.endsAt
                        ? dayjs(systemSettings.applicationFeeConfig.endsAt)
                        : null,
                    ]}
                    onChange={(dates: null | (Dayjs | null)[]) => {
                      setSystemSettings(prev => ({
                        ...prev,
                        applicationFeeConfig: {
                          ...(prev.applicationFeeConfig || {
                            applyMode: 'after_merchant_promo',
                            feeType: 'percent',
                            feeRate: 0,
                            feeAmountVnd: 0,
                          }),
                          startsAt:
                            dates?.[0]?.startOf('day').toISOString() ?? null,
                          endsAt:
                            dates?.[1]?.endOf('day').toISOString() ?? null,
                        },
                      }));
                    }}
                    format="DD/MM/YYYY"
                  />
                </Form.Item>

                <div style={{ color: '#888', fontSize: 12 }}>
                  Trạng thái hiện tại:{' '}
                  {systemSettings.applicationFeeConfig?.isActive
                    ? 'đang có hiệu lực'
                    : 'chưa áp dụng (ngoài thời gian hoặc phí = 0)'}
                  . Mỗi đơn thanh toán thành công sẽ lưu snapshot phí tại thời
                  điểm đó.
                </div>
              </Space>
            </div>

            <Divider />

            <div style={{ marginBottom: '24px' }}>
              <Title level={4}>Payment Gateway</Title>
              {user.role !== 'super_admin' && (
                <div style={{ color: '#888', fontSize: 12, marginBottom: 12 }}>
                  Chỉ Super Admin mới được thay đổi cấu hình payment gateway.
                </div>
              )}
              <Space
                direction="vertical"
                style={{ width: '100%' }}
                size="middle"
              >
                <Form.Item
                  label="Gateway thanh toán (toàn hệ thống)"
                  style={{ marginBottom: 0 }}
                  extra="AppotaPay là gateway thanh toán duy nhất của hệ thống."
                >
                  <Select
                    value={systemSettings.paymentGatewayProvider || 'appotapay'}
                    disabled={user.role !== 'super_admin'}
                    onChange={value =>
                      setSystemSettings(prev => ({
                        ...prev,
                        paymentGatewayProvider: value,
                      }))
                    }
                    style={{ width: 260 }}
                  >
                    <Option value="appotapay">AppotaPay</Option>
                  </Select>
                </Form.Item>

                <div style={{ color: '#888', fontSize: 12 }}>
                  AppotaPay:{' '}
                  {systemSettings.appotapayConfigured
                    ? 'đã cấu hình'
                    : 'chưa cấu hình'}
                </div>

                <Divider style={{ margin: '8px 0' }} />

                <Title level={5} style={{ margin: 0 }}>
                  AppotaPay
                </Title>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>Webhook / Redirect base URL</span>
                  <Input
                    value={systemSettings.paymentWebhookBaseUrl || ''}
                    disabled={user.role !== 'super_admin'}
                    placeholder="https://api.angi24.com/api/v1"
                    onChange={e =>
                      setSystemSettings(prev => ({
                        ...prev,
                        paymentWebhookBaseUrl: e.target.value,
                      }))
                    }
                    style={{ width: 320 }}
                  />
                </div>

                <Form.Item
                  label="Môi trường AppotaPay"
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    value={systemSettings.appotapayEnvironment || 'sandbox'}
                    disabled={user.role !== 'super_admin'}
                    onChange={value =>
                      setSystemSettings(prev => ({
                        ...prev,
                        appotapayEnvironment: value,
                      }))
                    }
                    style={{ width: 180 }}
                  >
                    <Option value="sandbox">Sandbox</Option>
                    <Option value="production">Production</Option>
                  </Select>
                </Form.Item>
                <Form.Item label="Partner Code" style={{ marginBottom: 0 }}>
                  <Input
                    value={systemSettings.appotapayPartnerCode || ''}
                    disabled={user.role !== 'super_admin'}
                    onChange={e =>
                      setSystemSettings(prev => ({
                        ...prev,
                        appotapayPartnerCode: e.target.value,
                      }))
                    }
                  />
                </Form.Item>
                <Form.Item label="API Key" style={{ marginBottom: 0 }}>
                  <Input.Password
                    value={systemSettings.appotapayApiKey || ''}
                    disabled={user.role !== 'super_admin'}
                    placeholder={
                      systemSettings.appotapayConfigured
                        ? 'Để trống nếu không đổi'
                        : 'Nhập API Key'
                    }
                    onChange={e =>
                      setSystemSettings(prev => ({
                        ...prev,
                        appotapayApiKey: e.target.value,
                      }))
                    }
                  />
                </Form.Item>
                <Form.Item label="Secret Key" style={{ marginBottom: 0 }}>
                  <Input.Password
                    value={systemSettings.appotapaySecretKey || ''}
                    disabled={user.role !== 'super_admin'}
                    placeholder={
                      systemSettings.appotapayConfigured
                        ? 'Để trống nếu không đổi'
                        : 'Nhập Secret Key'
                    }
                    onChange={e =>
                      setSystemSettings(prev => ({
                        ...prev,
                        appotapaySecretKey: e.target.value,
                      }))
                    }
                  />
                </Form.Item>
                <div style={{ color: '#888', fontSize: 12 }}>
                  IPN URL:{' '}
                  {(systemSettings.paymentWebhookBaseUrl ||
                    'https://api-staging.angi24.com/api/v1') +
                    '/appotapay/webhook/ipn'}
                </div>
              </Space>
            </div>

            <Divider />

            <div style={{ marginBottom: '24px' }}>
              <Title level={4}>Thời gian xử lý đơn (countdown)</Title>
              <div
                style={{ color: '#888', fontSize: '12px', marginBottom: 12 }}
              >
                Khi đơn ở mỗi trạng thái vượt quá thời gian dưới đây, app quán
                sẽ hiển thị cảnh báo trễ và popup xác nhận. Nếu quán không xử lý
                trong khoảng &quot;Thời gian chờ thêm&quot;, hệ thống tự huỷ đơn
                cho khách.
              </div>
              <Space
                direction="vertical"
                style={{ width: '100%' }}
                size="middle"
              >
                {[
                  {
                    key: 'orderSlaConfirmedMinutes' as const,
                    label: 'Đã xác nhận (confirmed)',
                  },
                  {
                    key: 'orderSlaPreparingMinutes' as const,
                    label: 'Đang chuẩn bị (preparing)',
                  },
                  {
                    key: 'orderSlaReadyMinutes' as const,
                    label: 'Sẵn sàng (ready)',
                  },
                ].map(row => (
                  <div
                    key={row.key}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>{row.label}</span>
                    <InputNumber
                      min={1}
                      max={1440}
                      value={systemSettings[row.key]}
                      onChange={value =>
                        setSystemSettings(prev => ({
                          ...prev,
                          [row.key]: value ?? 10,
                        }))
                      }
                      style={{ width: 120 }}
                      addonAfter="phút"
                    />
                  </div>
                ))}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>
                    Thời gian chờ thêm trước khi tự huỷ{' '}
                    <Tooltip title="Sau khi đơn vượt quá thời gian xử lý của trạng thái, quán có thêm khoảng thời gian này (popup) để bấm Tiếp tục hoặc Huỷ. Hết thời gian mà không có thao tác, hệ thống tự huỷ đơn.">
                      <QuestionCircleOutlined style={{ color: '#1890ff' }} />
                    </Tooltip>
                  </span>
                  <InputNumber
                    min={10}
                    max={600}
                    value={systemSettings.orderSlaGraceSeconds}
                    onChange={value =>
                      setSystemSettings(prev => ({
                        ...prev,
                        orderSlaGraceSeconds: value ?? 60,
                      }))
                    }
                    style={{ width: 120 }}
                    addonAfter="giây"
                  />
                </div>
              </Space>
            </div>

            <Divider />

            <div style={{ marginBottom: 24 }}>
              <Title level={5}>Tự động đánh giá 5 sao</Title>
              <div style={{ color: '#666', marginBottom: 12, fontSize: 13 }}>
                Sau khi đơn giao thành công, nếu khách không đánh giá trong
                khoảng thời gian này, hệ thống tự ghi nhận 5 sao cho quán.
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>Thời gian chờ trước khi tự đánh giá</span>
                <InputNumber
                  min={1}
                  max={168}
                  value={systemSettings.orderAutoReviewHours}
                  onChange={value =>
                    setSystemSettings(prev => ({
                      ...prev,
                      orderAutoReviewHours: value ?? 24,
                    }))
                  }
                  style={{ width: 120 }}
                  addonAfter="giờ"
                />
              </div>
            </div>

            <Divider />

            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSaveSystemSettings}
              loading={loading}
            >
              Lưu cài đặt
            </Button>
          </Card>
        </TabPane>
      </Tabs>

      <Divider />

      <Card>
        <div style={{ textAlign: 'center' }}>
          <Button danger size="large" onClick={handleLogout} loading={loading}>
            Đăng xuất
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default Settings;
